import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Save } from 'lucide-react';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const TIPO_OPTIONS = ['produttivo', 'ausiliario', 'funzionale', 'comune'];
const NATURA_OPTIONS = ['costo', 'ricavo', 'profitto'];

interface CentroCosto {
  id: string;
  codice: string;
  nome: string;
  tipo: string;
  natura: string;
  descrizione: string | null;
  attivo: boolean;
  responsabile_id: string | null;
}

interface Totali {
  dare: number;
  avere: number;
  saldo: number;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

const CentroCostoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [centro, setCentro] = useState<CentroCosto | null>(null);
  const [totali, setTotali] = useState<Totali | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Form state
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('produttivo');
  const [natura, setNatura] = useState('costo');
  const [descrizione, setDescrizione] = useState('');
  const [attivo, setAttivo] = useState(true);
  const [responsabileId, setResponsabileId] = useState('');

  const caricaDati = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const anno = new Date().getFullYear();
      const [centroRes, totaliRes] = await Promise.all([
        fetch(`/api/v1/centri-costo/centri/${id}`, { headers: authHeaders() }),
        fetch(`/api/v1/centri-costo/centri/${id}/totali?anno=${anno}`, { headers: authHeaders() }),
      ]);
      if (!centroRes.ok) throw new Error(`Errore caricamento centro: ${centroRes.status}`);
      const centroData: CentroCosto = await centroRes.json();
      setCentro(centroData);
      setNome(centroData.nome);
      setTipo(centroData.tipo);
      setNatura(centroData.natura);
      setDescrizione(centroData.descrizione ?? '');
      setAttivo(centroData.attivo);
      setResponsabileId(centroData.responsabile_id ?? '');

      if (totaliRes.ok) {
        const totaliData: Totali = await totaliRes.json();
        setTotali(totaliData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    caricaDati();
  }, [caricaDati]);

  const handleSave = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await fetch(`/api/v1/centri-costo/centri/${id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome,
          tipo,
          natura,
          descrizione: descrizione || null,
          attivo,
          responsabile_id: responsabileId || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Errore salvataggio: ${res.status}`);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  }, [id, nome, tipo, natura, descrizione, attivo, responsabileId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Caricamento…
      </div>
    );
  }

  if (error || !centro) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error ?? 'Centro non trovato'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          className="rounded-md p-1.5 hover:bg-gray-100"
          onClick={() => navigate('/centri-costo/centri')}
        >
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">{centro.codice} — {centro.nome}</h1>
          <p className="text-sm text-gray-500">{centro.tipo} / {centro.natura}</p>
        </div>
      </div>

      {/* Feedback */}
      {saveSuccess && (
        <div className="rounded-md border border-green-300 bg-green-50 px-4 py-2 text-sm text-green-700">
          Salvato con successo
        </div>
      )}
      {saveError && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">
          {saveError}
        </div>
      )}

      {/* Edit form */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Dati centro</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome</label>
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Responsabile ID</label>
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={responsabileId}
              onChange={(e) => setResponsabileId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              {TIPO_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Natura</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={natura}
              onChange={(e) => setNatura(e.target.value)}
            >
              {NATURA_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Descrizione</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={3}
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="attivo-check"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
              checked={attivo}
              onChange={(e) => setAttivo(e.target.checked)}
            />
            <label htmlFor="attivo-check" className="text-sm text-gray-700">Attivo</label>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            <Save className="h-4 w-4" />
            {saving ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>

      {/* Totali */}
      {totali && (
        <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">
            Totali {new Date().getFullYear()}
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-md bg-red-50 border border-red-100 p-3">
              <p className="text-xs text-red-600 font-medium uppercase tracking-wide mb-1">Dare</p>
              <p className="text-lg font-semibold tabular-nums text-red-700">{fmt(totali.dare)}</p>
            </div>
            <div className="rounded-md bg-green-50 border border-green-100 p-3">
              <p className="text-xs text-green-600 font-medium uppercase tracking-wide mb-1">Avere</p>
              <p className="text-lg font-semibold tabular-nums text-green-700">{fmt(totali.avere)}</p>
            </div>
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Saldo</p>
              <p className={`text-lg font-semibold tabular-nums ${totali.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {fmt(totali.saldo)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentroCostoDetail;
